
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PainelCreateRoleModal } from '@/components/painel/permissions/PainelCreateRoleModal';
import { PainelPermissionsEditorModal } from '@/components/painel/permissions/PainelPermissionsEditorModal';
import {
  BUILTIN_ROLE_VISUALS,
  CUSTOM_ROLE_VISUAL,
  PainelRoleIcon,
  PermissionCountBadge,
} from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelDescriptionClass,
  painelInsetCardClass,
  painelSurfaceAccentClass,
} from '@/components/painel/painel-styles';
import {
  createCustomPainelRole,
  getCustomPainelRoles,
  saveCustomPainelRoles,
  type CustomPainelRole,
} from '@/lib/painel-custom-roles';
import {
  PAINEL_ALL_PERMISSIONS,
  PAINEL_ROLE_DESCRIPTIONS,
  PAINEL_ROLE_LABELS,
  ROLE_PERMISSIONS,
  type PainelPermission,
  type PainelRole,
} from '@/lib/painel-rbac';
import { cn } from '@/lib/utils';
import { usePainelText } from '@/components/painel/painel-texts-context';

const BUILTIN_ROLE_ORDER: PainelRole[] = ['super_admin', 'admin', 'editor', 'reviewer'];

type EditingTarget =
  | { kind: 'builtin'; role: PainelRole }
  | { kind: 'custom'; role: CustomPainelRole };

export function PainelPermissoesPage() {
  const pageText = usePainelText('permissoes');
  const [customRoles, setCustomRoles] = useState<CustomPainelRole[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<EditingTarget | null>(null);

  const loadCustomRoles = useCallback(() => {
    setCustomRoles(getCustomPainelRoles());
  }, []);

  useEffect(() => {
    loadCustomRoles();
  }, [loadCustomRoles]);

  const totalPermissions = PAINEL_ALL_PERMISSIONS.length;

  const cards = useMemo(() => {
    const builtin = BUILTIN_ROLE_ORDER.map((role) => ({
      id: role,
      kind: 'builtin' as const,
      role,
      name: PAINEL_ROLE_LABELS[role],
      description: PAINEL_ROLE_DESCRIPTIONS[role],
      permissions: ROLE_PERMISSIONS[role],
      visual: BUILTIN_ROLE_VISUALS[role],
      isCustom: false,
    }));

    const custom = customRoles.map((role) => ({
      id: role.id,
      kind: 'custom' as const,
      role,
      name: role.name,
      description: role.description,
      permissions: role.permissions,
      visual: CUSTOM_ROLE_VISUAL,
      isCustom: true,
    }));

    return [...builtin, ...custom];
  }, [customRoles]);

  function handleCreateRole(input: {
    name: string;
    description: string;
    permissions: PainelPermission[];
  }) {
    const next = [...customRoles, createCustomPainelRole(input)];
    saveCustomPainelRoles(next);
    setCustomRoles(next);
  }

  const editingModal = editing
    ? {
        title: editing.kind === 'builtin' ? PAINEL_ROLE_LABELS[editing.role] : editing.role.name,
        subtitle:
          editing.kind === 'builtin'
            ? PAINEL_ROLE_DESCRIPTIONS[editing.role]
            : editing.role.description,
        visual:
          editing.kind === 'builtin'
            ? BUILTIN_ROLE_VISUALS[editing.role]
            : CUSTOM_ROLE_VISUAL,
        permissions:
          editing.kind === 'builtin'
            ? ROLE_PERMISSIONS[editing.role]
            : editing.role.permissions,
      }
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
            System
          </p>
          <h1 className="mt-1 font-poppins text-2xl font-bold text-white md:text-3xl">{pageText.title}</h1>
          <p className={cn('mt-2 max-w-2xl text-sm', painelDescriptionClass)}>{pageText.description}</p>
        </div>
        <Button
          type="button"
          className={cn(painelButtonPrimaryClass, 'h-11 shrink-0 px-5')}
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nova permissão / cargo
        </Button>
      </div>

      <div
        className={cn(
          painelSurfaceAccentClass,
          'flex flex-wrap items-center gap-4 px-5 py-4 sm:px-6'
        )}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/15 ring-1 ring-sky-400/25">
          <Sparkles className="h-5 w-5 text-sky-300" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-poppins text-sm font-semibold text-white">
            {cards.length} cargos · {totalPermissions} permissões no sistema
          </p>
          <p className="font-inter text-xs text-slate-400">
            {customRoles.length > 0
              ? `${customRoles.length} cargo(s) personalizado(s) neste dispositivo`
              : 'Crie cargos customizados para testar combinações de permissões'}
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {cards.map((card, index) => (
          <motion.article
            key={card.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={cn(
              painelInsetCardClass,
              'group relative flex flex-col gap-5 overflow-hidden p-6 transition-all duration-300',
              'hover:border-sky-400/30 hover:bg-white/[0.06] hover:shadow-[0_0_40px_-12px_rgba(56,189,248,0.22)]'
            )}
          >
            <div
              className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-sky-500/10 blur-2xl transition-opacity group-hover:opacity-100"
              aria-hidden
            />

            <div className="relative flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <PainelRoleIcon config={card.visual} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-poppins text-lg font-bold text-white">{card.name}</h2>
                    {card.isCustom ? (
                      <span className="rounded-full border border-indigo-400/30 bg-indigo-500/15 px-2 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wide text-indigo-200">
                        Custom
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 font-inter text-sm leading-relaxed text-slate-400">
                    {card.description}
                  </p>
                </div>
              </div>
            </div>

            <PermissionCountBadge active={card.permissions.length} total={totalPermissions} />

            <Button
              type="button"
              variant="outline"
              className={cn(
                painelButtonOutlineClass,
                'relative mt-auto h-10 w-full border-white/15 bg-white/[0.03] text-blue-100 hover:bg-white/10 hover:text-white'
              )}
              onClick={() =>
                setEditing(
                  card.kind === 'builtin'
                    ? { kind: 'builtin', role: card.role }
                    : { kind: 'custom', role: card.role }
                )
              }
            >
              Editar permissões
            </Button>
          </motion.article>
        ))}
      </div>

      <PainelCreateRoleModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreateRole}
      />

      {editingModal ? (
        <PainelPermissionsEditorModal
          open={editing !== null}
          onOpenChange={(open) => !open && setEditing(null)}
          title={editingModal.title}
          subtitle={editingModal.subtitle}
          visual={editingModal.visual}
          enabledPermissions={editingModal.permissions}
          readOnly
        />
      ) : null}
    </div>
  );
}
